import React, { useEffect, useState } from "react";
import { api, mediaApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Mail, Phone, Download, Briefcase, Calendar } from "lucide-react";

const Applications = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/api/applications");
        if (alive) setApps(data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this application? The CV will also be removed.")) return;
    await api.delete(`/api/applications/${id}`);
    setApps((a) => a.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const downloadCv = async (app) => {
    try {
      const res = await mediaApi.get(`/api/applications/${app.id}/cv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = app.cv_filename || "cv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download CV");
    }
  };

  if (loading)
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-zinc-400" /></div>;

  return (
    <div data-testid="applications-page">
      <h1 className="text-2xl font-medium text-zinc-900">Applications</h1>
      <p className="text-sm text-zinc-500 mt-1">Job applications submitted from the Careers page.</p>

      {apps.length === 0 ? (
        <div className="mt-8 text-center py-20 text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-lg">No applications yet.</div>
      ) : (
        <ul className="mt-8 space-y-4">
          {apps.map((a) => (
            <li key={a.id} data-testid={`application-${a.id}`} className="border border-zinc-200 rounded-lg bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-900">{a.firstName} {a.lastName}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"><Briefcase size={10} /> {a.position}</span>
                    {a.availability && <span className="text-[10px] tracking-wider uppercase text-zinc-600 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-full">{a.availability}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-600">
                    {a.email && <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-zinc-900"><Mail size={12} /> {a.email}</a>}
                    {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 hover:text-zinc-900"><Phone size={12} /> {a.phone}</a>}
                    {a.startDate && <span className="inline-flex items-center gap-1"><Calendar size={12} /> Starts {a.startDate}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => downloadCv(a)} data-testid={`application-cv-${a.id}`} className="inline-flex items-center gap-1.5 text-xs text-zinc-700 hover:text-zinc-900 border border-zinc-300 rounded-md px-3 py-1.5">
                    <Download size={13} /> CV
                  </button>
                  <button onClick={() => onDelete(a.id)} data-testid={`application-delete-${a.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-zinc-400">{new Date(a.created_at).toLocaleString()} · {a.cv_filename}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Applications;
